/**
 * Vera Share - typed BFF client.
 *
 * Mirrors packages/bff/src/share (camelCase over the wire). Envelope
 * convention: reads return a named-key object which this layer unwraps; errors
 * are `{ error }` and are thrown as Error(message).
 *
 * Note there is no client function for a file's CONTENT. The bytes are never
 * fetched into the SPA: the viewer page points a sandboxed iframe straight at
 * /api/share/content/:slug so the document loads on an opaque origin and can
 * never touch the session. Pulling it through fetch and injecting it would
 * undo the entire security model.
 */

import { bffFetch } from '@shared/api/bffFetch';

export interface ShareFileMeta {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  size: number;
  active: boolean;
  /** 'YYYY-MM-DD'. Serves through this date, then stops. */
  expiresAt: string;
  publishedByOid: string;
  publishedByName: string;
  createdAt: string;
  updatedAt: string;
  revision: number;
}

export interface ShareFileForRecipient extends ShareFileMeta {
  lastViewedAt: string | null;
}

export interface ShareFileForPublisher extends ShareFileMeta {
  recipientCount: number;
  viewedCount: number;
}

export interface ShareRecipient {
  personOid: string;
  personName: string;
  addedByOid: string;
  addedByName: string;
  addedAt: string;
  lastViewedAt: string | null;
  viewCount: number;
}

export interface ShareMeta {
  me: {
    oid: string;
    name: string;
    /** capability share.publish */
    canPublish: boolean;
    /** capability share.admin */
    canAdmin: boolean;
  };
}

export interface PublishInput {
  slug: string;
  title: string;
  description: string | null;
  html: string;
  expiresAt: string;
}

export interface FilePatch {
  title?: string;
  description?: string | null;
  active?: boolean;
  expiresAt?: string;
}

/** The URL the viewer page frames. Never fetched by the SPA itself. */
export const contentUrl = (slug: string, revision: number): string =>
  `/api/share/content/${encodeURIComponent(slug)}?r=${revision}`;

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function readError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error || `${fallback} (${res.status})`);
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await bffFetch(path);
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

async function sendJson<T>(method: string, path: string, body: unknown, fallback: string): Promise<T> {
  const res = await bffFetch(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, fallback);
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const fetchMeta = () => getJson<ShareMeta>('/api/share/meta', 'Failed to load Vera Share');

/** "Shared with me": active, unexpired files naming the signed-in user. */
export const fetchMyFiles = () =>
  getJson<{ files: ShareFileForRecipient[] }>(
    '/api/share/files',
    'Failed to load your shared files'
  ).then((r) => r.files);

export const fetchFile = (slug: string) =>
  getJson<{ file: ShareFileMeta }>(
    `/api/share/files/${encodeURIComponent(slug)}`,
    'Failed to open that file'
  ).then((r) => r.file);

/** Files the caller published (every file, for a share admin). */
export const fetchPublished = () =>
  getJson<{ files: ShareFileForPublisher[] }>(
    '/api/share/published',
    'Failed to load your published files'
  ).then((r) => r.files);

export const fetchRecipients = (fileId: string) =>
  getJson<{ recipients: ShareRecipient[] }>(
    `/api/share/published/${fileId}/recipients`,
    'Failed to load the recipient list'
  ).then((r) => r.recipients);

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export const publishFile = (input: PublishInput) =>
  sendJson<{ file: ShareFileMeta }>('POST', '/api/share/published', input, 'Failed to publish').then(
    (r) => r.file
  );

export const patchFile = (fileId: string, patch: FilePatch) =>
  sendJson<{ file: ShareFileMeta }>(
    'PATCH',
    `/api/share/published/${fileId}`,
    patch,
    'Failed to update that file'
  ).then((r) => r.file);

/** Replace the content in place: same link, same recipients, new bytes. */
export const replaceContent = (fileId: string, html: string) =>
  sendJson<{ file: ShareFileMeta }>(
    'PUT',
    `/api/share/published/${fileId}/content`,
    { html },
    'Failed to replace the file'
  ).then((r) => r.file);

export const deleteFile = async (fileId: string) => {
  const res = await bffFetch(`/api/share/published/${fileId}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to delete that file');
};

export const addRecipient = (fileId: string, personOid: string, personName: string) =>
  sendJson<{ recipients: ShareRecipient[] }>(
    'POST',
    `/api/share/published/${fileId}/recipients`,
    { personOid, personName },
    'Failed to add that recipient'
  ).then((r) => r.recipients);

export const removeRecipient = async (fileId: string, personOid: string) => {
  const res = await bffFetch(
    `/api/share/published/${fileId}/recipients/${encodeURIComponent(personOid)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) await readError(res, 'Failed to remove that recipient');
  return ((await res.json()) as { recipients: ShareRecipient[] }).recipients;
};
